import download from 'downloadjs';
import { nanoid } from 'nanoid';
import type { JsonObject, JsonValue } from 'type-fest';

import { prepareAndMakeRequest } from '@frontend/api';
import type {
  QuoteListApiFilters,
  MetadataKey,
  PrepareAndMakeRequestOptions,
  RequestQuery,
  QuoteItemBaqData,
  SingleComponentNestingInput,
  SingleComponentNestingResults,
  SingleComponentNestingStatus,
  QuoteQpvData,
} from '@frontend/api';

import type { DateFilters } from 'services/transformations';

import { toNumberOrNull } from 'utils/numberUtils';

import {
  transFormDateFieldToFilter,
  transformMetadataToFilter,
  transformRelativeDateToFilter,
} from './transformations';

type FilterValue = number | string | string[];

export type NonMetadataFilters = {
  accountIn?: number[];
  accountIsUnassigned?: boolean;
  contactIn?: number[];
  contactIsUnassigned?: boolean;
  estimatorIn?: number[];
  estimatorIsUnassigned?: boolean;
  opDefIn?: number[];
  priority?: number;
  processIn?: number[];
  salespersonIn?: number[];
  salespersonIsUnassigned?: boolean;
  totalPrice?: number;
};

export type Filters = Record<MetadataKey, string> &
  NonMetadataFilters &
  DateFilters;

export const transformFiltersParamsToApi = (
  filters: Filters,
): QuoteListApiFilters => {
  const metadataKeys = Object.keys(filters).filter((key) =>
    key.startsWith('metadata'),
  ) as MetadataKey[];
  const ret: Omit<QuoteListApiFilters, MetadataKey> = {
    account_in:
      filters.accountIn &&
      Array.isArray(filters.accountIn) &&
      filters.accountIn.length > 0
        ? filters.accountIn.join(',')
        : undefined,
    account_is_unassigned: filters.accountIsUnassigned
      ? filters.accountIsUnassigned
      : undefined,
    contact_in:
      filters.contactIn &&
      Array.isArray(filters.contactIn) &&
      filters.contactIn.length > 0
        ? filters.contactIn.join(',')
        : undefined,
    contact_is_unassigned: filters.contactIsUnassigned
      ? filters.contactIsUnassigned
      : undefined,
    estimator_in:
      filters.estimatorIn &&
      Array.isArray(filters.estimatorIn) &&
      filters.estimatorIn.length > 0
        ? filters.estimatorIn.join(',')
        : undefined,
    estimator_is_unassigned: filters.estimatorIsUnassigned
      ? filters.estimatorIsUnassigned
      : undefined,
    salesperson_in:
      filters.salespersonIn &&
      Array.isArray(filters.salespersonIn) &&
      filters.salespersonIn.length > 0
        ? filters.salespersonIn.join(',')
        : undefined,
    salesperson_is_unassigned: filters.salespersonIsUnassigned
      ? filters.salespersonIsUnassigned
      : undefined,
    total_price: filters.totalPrice || undefined,
    priority: filters.priority || undefined,
    process_in:
      filters.processIn &&
      Array.isArray(filters.processIn) &&
      filters.processIn.length > 0
        ? filters.processIn.join(',')
        : undefined,
    op_def_in:
      filters.opDefIn &&
      Array.isArray(filters.opDefIn) &&
      filters.opDefIn.length > 0
        ? filters.opDefIn.join(',')
        : undefined,
    ...transFormDateFieldToFilter('created', filters.createdDateFilter),
    ...transFormDateFieldToFilter('ordered', filters.orderedDateFilter),
    ...transFormDateFieldToFilter('sent', filters.sentDateFilter),
    ...transFormDateFieldToFilter('viewed', filters.viewedDateFilter),
    ...transFormDateFieldToFilter('expired', filters.expiredDateFilter),
    ...transFormDateFieldToFilter('due', filters.dueDateFilter),
  };
  let meta: DateFilters & Record<MetadataKey | 'metadata', string> = {};
  for (const key of metadataKeys) {
    if (key === 'metadata')
      meta = {
        ...meta,
        ...(transformMetadataToFilter(filters[key]) || undefined),
      };
    else meta[key] = filters[key];
  }
  return {
    ...ret,
    ...meta,
  };
};

export const transformApiToFiltersParams = (
  filters: QuoteListApiFilters,
): Filters => {
  const accountIn: number[] = [];
  let accountIsUnassigned = false;
  const contactIn: number[] = [];
  let contactIsUnassigned = false;
  const estimatorIn: number[] = [];
  let estimatorIsUnassigned = false;
  const salespersonIn: number[] = [];
  let salespersonIsUnassigned = false;
  let priority: number | undefined = undefined;
  const processIn: number[] = [];
  const opDefIn: number[] = [];

  if (typeof filters.account_in === 'string') {
    filters.account_in.split(',').forEach((accountId) => {
      if (toNumberOrNull(accountId)) {
        accountIn.push(Number(accountId));
      }
    });
  }

  if (typeof filters.contact_in === 'string') {
    filters.contact_in.split(',').forEach((contactId) => {
      if (toNumberOrNull(contactId)) {
        contactIn.push(Number(contactId));
      }
    });
  }

  if (typeof filters.estimator_in === 'string') {
    filters.estimator_in.split(',').forEach((estimatorId) => {
      if (toNumberOrNull(estimatorId)) {
        estimatorIn.push(Number(estimatorId));
      }
    });
  }

  if (typeof filters.salesperson_in === 'string') {
    filters.salesperson_in.split(',').forEach((salespersonId) => {
      if (toNumberOrNull(salespersonId)) {
        salespersonIn.push(Number(salespersonId));
      }
    });
  }

  if (typeof filters.op_def_in === 'string') {
    filters.op_def_in.split(',').forEach((opDefId) => {
      if (toNumberOrNull(opDefId)) {
        opDefIn.push(Number(opDefId));
      }
    });
  }

  if (typeof filters.process_in === 'string') {
    filters.process_in.split(',').forEach((processId) => {
      if (toNumberOrNull(processId)) {
        processIn.push(Number(processId));
      }
    });
  }

  if (
    typeof filters.account_is_unassigned === 'string' &&
    filters.account_is_unassigned.toLowerCase() === 'true'
  ) {
    accountIsUnassigned = true;
  }

  if (
    typeof filters.contact_is_unassigned === 'string' &&
    filters.contact_is_unassigned.toLowerCase() === 'true'
  ) {
    contactIsUnassigned = true;
  }

  if (
    typeof filters.estimator_is_unassigned === 'string' &&
    filters.estimator_is_unassigned.toLowerCase() === 'true'
  ) {
    estimatorIsUnassigned = true;
  }

  if (typeof filters.priority === 'string') {
    const parsedPriority = Number.parseInt(filters.priority, 10);
    if (!Number.isNaN(parsedPriority)) {
      priority = parsedPriority;
    }
  }

  if (
    typeof filters.salesperson_is_unassigned === 'string' &&
    filters.salesperson_is_unassigned.toLowerCase() === 'true'
  ) {
    salespersonIsUnassigned = true;
  }

  const metadataFilters: Record<MetadataKey, FilterValue> = {};
  for (const key in filters) {
    if (key.startsWith('metadata'))
      metadataFilters[key as MetadataKey] = filters[key as MetadataKey];
  }

  return {
    accountIn,
    accountIsUnassigned,
    contactIn,
    contactIsUnassigned,
    estimatorIn,
    estimatorIsUnassigned,
    opDefIn,
    processIn,
    priority,
    salespersonIn,
    salespersonIsUnassigned,
    ...transformRelativeDateToFilter(filters),
    ...metadataFilters,
  };
};

const addOns = {
  create: (componentId: string) =>
    prepareAndMakeRequest.post(`/quotes/components/${componentId}/add_ons/`),
  delete: (addOnId: string) =>
    prepareAndMakeRequest.delete(`/quotes/components/add_ons/${addOnId}`),
  update: (addOnId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/components/add_ons/${addOnId}`, {
      body: data,
    }),
};

const additionalCharges = {
  create: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/add_ons?is_for_facilitated_order=true`,
      { body: data },
    ),
  updateCell: (addOnCellId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/add_on_cells/${addOnCellId}?is_for_facilitated_order=true`,
      { body: data },
    ),
  update: (addOnId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/add_ons/${addOnId}?is_for_facilitated_order=true`,
      { body: data },
    ),
};

const adjustments = {
  create: (
    componentId: string,
    quantity: {
      quantity: string;
      isForFacilitatedOrder: boolean;
      manualLeadTime?: string | null;
      manualTotalUnitPrice?: string | string;
    },
  ) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/quantities?is_for_facilitated_order=true`,
      { body: { ...quantity } },
    ),
};

const addOnCells = {
  update: (addOnCellId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/add_on_cells/${addOnCellId}`,
      { body: data },
    ),
};

const componentNesting = {
  get: (componentId: number): Promise<SingleComponentNestingResults[]> =>
    prepareAndMakeRequest.get(`/quotes/components/${componentId}/nesting/`),

  request: (
    componentId: number,
    data: SingleComponentNestingInput,
  ): Promise<{ taskHash: string }> =>
    prepareAndMakeRequest.post(`/quotes/components/${componentId}/nesting/`, {
      body: data,
    }),

  status: (
    componentId: number,
    taskHash: string,
  ): Promise<SingleComponentNestingStatus> =>
    prepareAndMakeRequest.get(
      `/quotes/components/${componentId}/nesting/status?task_hash=${taskHash}`,
    ),
};

const components = {
  get: (componentId: string, nodeId?: string) =>
    prepareAndMakeRequest.get(
      `/quotes/components/${componentId}`,
      nodeId ? { query: { nodeId } } : {},
    ),

  getBrief: (componentId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/brief/${componentId}`),

  update: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/components/${componentId}`, {
      body: data,
    }),

  refreshBomAvailability: (rootComponentId: string) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${rootComponentId}/bom_availability`,
    ),

  setComponentNodeQuantity: (quoteItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(`/quotes/items/${quoteItemId}/node_count`, {
      body: data,
    }),

  regenerate: (componentId: string) =>
    prepareAndMakeRequest.post(`/quotes/components/${componentId}/regenerate`),

  importFromJob: (data: JsonObject) =>
    prepareAndMakeRequest.post(
      '/quotes/components/import_job_routing_and_pricing',
      { body: data },
    ),

  importFromQuote: (data: JsonObject) =>
    prepareAndMakeRequest.post(
      '/quotes/components/import_quote_routing_and_pricing',
      { body: data },
    ),

  purchasedComponentsSearch: (
    componentId: string,
    brands?: string | string[],
  ) =>
    prepareAndMakeRequest.get(
      `/quotes/components/${componentId}/purchased_components_search`,
      brands ? { query: { brands } } : {},
    ),

  replaceParent: (partUuid: string) =>
    prepareAndMakeRequest.post(`/quotes/components/${partUuid}/replace_parent`),
};

const quantities = {
  create: (componentId: string, quantity: number) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/quantities/`,
      { body: { quantity } },
    ),

  delete: (quantityId: string) =>
    prepareAndMakeRequest.delete(`/quotes/components/quantities/${quantityId}`),

  get: (quantityId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/quantities/${quantityId}`),

  list: (componentId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/${componentId}/quantities/`),

  update: (quantityId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/components/quantities/${quantityId}`, {
      body: data,
    }),

  bulkUpdate: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/${componentId}/bulk_update_quantities`,
      { body: data },
    ),

  bulkCRUD: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(`/quotes/components/${componentId}/update`, {
      body: data,
    }),

  getRealTimeMaterialPricing: (componentId: string, params: RequestQuery) =>
    prepareAndMakeRequest.get(
      `/quotes/components/${componentId}/real_time_material_pricing/`,
      { query: params },
    ),
};

const quoteItems = {
  update: (quoteItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/items/${quoteItemId}`, { body: data }),

  updatePrivateNotes: (quoteItemId: string, text: string) =>
    prepareAndMakeRequest.post(`/quotes/items/${quoteItemId}/private_notes`, {
      body: { text },
    }),

  updatePublicNotes: (quoteItemId: string, text: string) =>
    prepareAndMakeRequest.post(`/quotes/items/${quoteItemId}/public_notes`, {
      body: { text },
    }),

  delete: (quoteItemId: string) =>
    prepareAndMakeRequest.delete(`/quotes/items/${quoteItemId}`),

  bulkDelete: (quoteId: string, quoteItems: string[]) =>
    prepareAndMakeRequest.delete(`/quotes/items/${quoteId}/bulk_delete`, {
      body: { quoteItems },
    }),

  createExpedite: (quantityId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/quantities/${quantityId}/expedites`,
      { body: data },
    ),

  updateExpedite: (expediteId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/quantities/expedites/${expediteId}`,
      { body: data },
    ),

  deleteExpedite: (expediteId: string) =>
    prepareAndMakeRequest.delete(
      `/quotes/components/quantities/expedites/${expediteId}`,
    ),

  getDigiQuoteViewerData: (
    quoteItemId: string,
    partFileUuid: string,
    fetchOptions: PrepareAndMakeRequestOptions,
  ) =>
    prepareAndMakeRequest(
      `/quotes/items/${quoteItemId}/part_file/${partFileUuid}/viewer_data`,
      fetchOptions,
    ),

  get: (quoteItemId: string, params: RequestQuery) =>
    prepareAndMakeRequest.get(`/quotes/items/${quoteItemId}`, {
      query: params,
    }),

  makeAssembly: (
    quoteItemId: string,
    {
      shouldRegenerateOps,
      shouldMakeSupportingFileIfHasGeometry,
    }: {
      shouldRegenerateOps: boolean;
      shouldMakeSupportingFileIfHasGeometry: boolean;
    },
  ) =>
    prepareAndMakeRequest.post(
      `/quotes/items/${quoteItemId}/convert_part_to_assembly`,
      { body: { shouldRegenerateOps, shouldMakeSupportingFileIfHasGeometry } },
    ),

  mergeItemsAsSupportingFiles: ({
    quoteId,
    parentQuoteItemId,
    childrenQuoteItemIds,
  }: {
    quoteId: string;
    parentQuoteItemId: string;
    childrenQuoteItemIds: string[];
  }) =>
    prepareAndMakeRequest.post(
      `/quotes/${quoteId}/merge_items_as_supporting_files`,
      {
        body: {
          parentId: parentQuoteItemId,
          childrenIds: childrenQuoteItemIds,
        },
      },
    ),

  mergeItemsAsComponents: ({
    quoteId,
    parentQuoteItemId,
    childrenQuoteItemIds,
    shouldRegenerateOps,
  }: {
    quoteId: string;
    parentQuoteItemId: string;
    childrenQuoteItemIds: string[];
    shouldRegenerateOps: boolean;
  }) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/merge_items_as_components`, {
      body: {
        parentId: parentQuoteItemId,
        childrenIds: childrenQuoteItemIds,
        shouldRegenerateOps,
      },
    }),

  deepCopy: (quoteItemId: string) =>
    prepareAndMakeRequest.post(`/quotes/items/${quoteItemId}/part_deep_copy`),

  deepCopyInPlace: (
    quoteId: string,
    { quoteItemIds }: { quoteItemIds: string[] },
  ) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/copy_quote_items_in_place`, {
      body: { quoteItemIds },
    }),
};

const quoteViewer = {
  get: (quoteNum: number, query: { group: string; revisionNumber?: number }) =>
    prepareAndMakeRequest.get<QuoteQpvData>(`/quotes/viewer/${quoteNum}`, {
      query,
    }),
};

export default {
  quantities,
  quoteItems,
  quoteViewer,
  components,
  componentNesting,
  addOns,
  addOnCells,
  additionalCharges,
  adjustments,

  list: async (groupId: string, params: Exclude<RequestQuery, string>) => {
    const response = await prepareAndMakeRequest.get<JsonObject>(
      `/quotes/groups/${groupId}`,
      { query: params },
    );
    return {
      ...response,
      filters: {},
    };
  },
  copyPricing: (sourceComponentId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${sourceComponentId}/copy_pricing`,
      { body: data },
    ),
  applyQuoteExpediteOptions: (quoteId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/${quoteId}/apply_by_expedite_templates`,
      { body: data },
    ),

  clearQuoteExpediteOptions: (quoteId: string) =>
    prepareAndMakeRequest.post(
      `/quotes/${quoteId}/clear_by_expedite_templates`,
    ),

  copyPartsToQuote: (quoteId: string, data: JsonObject) =>
    prepareAndMakeRequest.put(`/quotes/${quoteId}/copy_parts`, { body: data }),

  copyQuoteItemsInQuote: (
    quoteId: string,
    { quoteItemIds }: { quoteItemIds: string[] },
  ) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/copy_quote_items`, {
      body: { quoteItemIds },
    }),

  getHeaderByNumber: (quoteNumberWithRevision: string) =>
    prepareAndMakeRequest.get(
      `/quotes/by_number/${quoteNumberWithRevision}/header`,
    ),

  getQuoteItemHeadersByNumber: (
    quoteNumberWithRevision: string,
    quoteItemIds?: string[],
  ): Promise<QuoteItemBaqData[]> =>
    prepareAndMakeRequest.get(
      `/quotes/by_number/${quoteNumberWithRevision}/quote_items`,
      {
        query: quoteItemIds ? { quoteItemIds } : {},
      },
    ),

  getHeaderById: (quoteId: string) =>
    prepareAndMakeRequest.get(`/quotes/${quoteId}/header`),

  getQuoteItemHeadersById: (
    quoteId: string,
    quoteItemIds?: string[],
  ): Promise<QuoteItemBaqData[]> =>
    prepareAndMakeRequest.get(`/quotes/${quoteId}/quote_items`, {
      query: quoteItemIds ? { quoteItemIds } : {},
    }),

  getByToken: async (token: string, isPreview: boolean) => {
    const params = isPreview ? { mode: 'preview' } : undefined;
    const response = await prepareAndMakeRequest.get<{
      quoteItems?: { rootComponent: { part: JsonObject & { uuid: string } } }[];
    }>(`/quotes/key/${token}`, params && { query: params });
    response.quoteItems
      ? response.quoteItems.forEach(
          (quoteItem) =>
            quoteItem.rootComponent.part &&
            (quoteItem.rootComponent.part.uuid = nanoid()),
        )
      : (response.quoteItems = []);
    return response;
  },

  getEmailTemplates: async (quoteId: string) => {
    const response = await prepareAndMakeRequest.get<{ results: JsonValue[] }>(
      `/quotes/${quoteId}/emails/`,
    );
    return { emailTemplates: response.results };
  },

  create: (groupId: string) =>
    prepareAndMakeRequest.post(`/quotes/groups/${groupId}`),

  requestStatusChange: (groupId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/${groupId}/request_status_change`, {
      body: data,
    }),

  updatePrivateNotes: (quoteId: string, text: string) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/private_notes`, {
      body: { text },
    }),

  updateCustomerFacingNotes: (quoteId: string, text: string) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/quote_notes`, {
      body: { text },
    }),

  update: (quoteNumberWithRevision: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/by_number/${quoteNumberWithRevision}/header`,
      { body: data },
    ),

  getRelatedRevisions: (quoteId: string) =>
    prepareAndMakeRequest.get(`/quotes/${quoteId}/related_revisions`),

  createRevision: (quoteId: string) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/create_revision`),

  copy: (quoteId: string, requoteToken?: string) =>
    prepareAndMakeRequest.post(
      `/quotes/${quoteId}/copy${
        requoteToken ? `?requote_token=${requoteToken}` : ''
      }`,
    ),

  send: (quoteId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/send`, {
      body: {
        url: data.url,
        bcc: data.bcc,
        cc: data.cc,
        to: data.to,
        body: data.body,
        subject: data.subject,
        include_quote_pdf: data.includeQuotePdf,
      },
    }),

  getGlobalExpediteOptions: async (groupId: string) => {
    const response = await prepareAndMakeRequest.get<{
      results: { id: number }[];
    }>(`/suppliers/${groupId}/expedite_templates`);
    return response.results.sort((a, b) => a.id - b.id);
  },

  createGlobalExpediteOption: (groupId: string, expediteOption: JsonObject) =>
    prepareAndMakeRequest.post(`/suppliers/${groupId}/expedite_templates`, {
      body: expediteOption,
    }),

  updateGlobalExpediteOption: (
    expediteOptionId: string,
    expediteOption: JsonObject,
  ) =>
    prepareAndMakeRequest.patch(
      `/suppliers/expedite_templates/${expediteOptionId}`,
      { body: expediteOption },
    ),

  deleteGlobalExpediteOption: (expediteOptionId: string) =>
    prepareAndMakeRequest.delete(
      `/suppliers/expedite_templates/${expediteOptionId}`,
    ),

  resolveContactConflict: (requestForQuoteId: string) =>
    prepareAndMakeRequest.patch(`/rfq/${requestForQuoteId}`, {
      body: {
        contact_info_conflict: false,
      },
    }),

  requestARequote: (token: string) =>
    prepareAndMakeRequest.post(`/quotes/key/${token}/requote`),

  downloadNestPDF: (quoteId: string, nestId: number) =>
    prepareAndMakeRequest.post(
      `/quotes/nesting/${quoteId}/multi_component_nest_download_pdf`,
      {
        body: { nest_id: nestId },
      },
    ),

  getShippingInfoForFinalize: (quoteId: string) =>
    prepareAndMakeRequest.get(`/quotes/${quoteId}/can_estimate_shipping`),

  downloadCsvByNumber: async (
    quoteNumber: string,
    params: { revisionNumber: string | number },
  ) => {
    const response = await prepareAndMakeRequest.get<Blob>(
      `/quotes/csv_by_number/${quoteNumber}`,
      { query: params },
    );
    const blob = new Blob([response], {
      type: 'text/plain',
    });
    const fileName = params.revisionNumber
      ? `quote${quoteNumber}-${params.revisionNumber}.csv`
      : `quote${quoteNumber}.csv`;
    download(blob, fileName);
  },

  initiateExportQuoteSummary: (
    quoteId: string,
    fetchOptions: PrepareAndMakeRequestOptions,
  ) =>
    prepareAndMakeRequest.get<Blob>(`/quotes/${quoteId}/export`, fetchOptions),

  exportQuoteSummary: async (
    taskId: string,
    quoteNumber: string | number,
    quoteRevision: string,
    fetchOptions?: PrepareAndMakeRequestOptions,
  ) => {
    const response = await prepareAndMakeRequest.get<Blob>(
      `/quotes/download_quote_summary_from_task/${taskId}`,
      fetchOptions,
    );
    if (fetchOptions?.signal?.aborted) {
      return;
    }
    const filename = quoteRevision
      ? `quote_${quoteNumber}_${quoteRevision}_summary.xlsx`
      : `quote_${quoteNumber}_summary.xlsx`;
    download(
      response,
      filename,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  },
};
